/**
 * IonTierLimitsPanel — runtime editor for the Ion (AI chat) per-tier limits.
 * =========================================================================
 * Surfaces the backend `/admin/chat-management/tier-limits` contract:
 *   - daily_usd / weekly_usd / monthly_usd — soft cost budgets per tier
 * Each field shows env-default vs DB-override (OVERRIDDEN / DEFAULT badge),
 * is editable inline, and can be reset to its env default (sends `null`).
 * A read-only "≈ N tokens" caption (server-computed `*_tokens` field,
 * $0.25/token — `chat/token_units.py`) rides alongside each $ field so
 * magnitudes are legible without doing the division by hand (2026-07-17,
 * Ion limits UX pass — customer-facing surfaces stay tokens-only; this is
 * an admin-only convenience, the $ figure is still what's edited).
 *
 * NOTE (2026-07-17): the per-day REQUEST-COUNT cap (`daily_requests`, "AI
 * questions per day") was removed entirely — the USD caps below are the
 * sole enforcement mechanism now. Do not resurrect that field.
 *
 * Shared by both admin surfaces so the two can't drift:
 *   - AdminDemoTab       → tier="demo"  (BA wants demo Ion limits under Demo)
 *   - AdminAiManagementTab → tier="pro" (Tier limits panel in AI Management)
 *
 * Mirrors the AdminDemoTab demo_config config-editor rows exactly (same
 * .admin-demo-* classes, inline success/error alerts, per-field save/reset).
 */
import { useEffect, useState } from 'preact/hooks';
import { getChatTierLimits, updateChatTierLimits } from '../../services/adminService.js';

// Editable fields per tier (order = display order). All USD decimals.
// `tokenKey` (if present) is the matching read-only token field on the same
// GET response row, rendered as a "≈ N tokens" caption next to the input.
const TIER_FIELDS = [
  {
    key: 'daily_usd',
    tokenKey: 'daily_tokens',
    label: 'Daily cost budget ($)',
    help: 'Soft daily USD budget across a user’s Ion usage before requests are blocked.',
    kind: 'usd',
    min: 0,
    max: 10000,
    step: 0.01,
  },
  {
    key: 'weekly_usd',
    tokenKey: 'weekly_tokens',
    label: 'Weekly cost budget ($)',
    help: 'Soft weekly USD budget across a user’s Ion usage before requests are blocked.',
    kind: 'usd',
    min: 0,
    max: 100000,
    step: 0.01,
  },
  {
    key: 'monthly_usd',
    tokenKey: 'monthly_tokens',
    label: 'Monthly cost budget ($)',
    help:
      'The plan allotment for this tier/profile — admin-only for every profile ' +
      '(users can self-service daily/weekly pacing within this, but never this ' +
      'figure itself). For DEMO this is a one-time total-for-the-account ceiling, ' +
      'not a monthly reset.',
    kind: 'usd',
    min: 0,
    max: 100000,
    step: 0.01,
  },
];

/**
 * @param {object} props
 * @param {'demo'|'pro'|'batemo'|'unlimited'} props.tier — which tier's limits to edit.
 * @param {string} [props.heading] — section heading text.
 * @param {string[]} [props.fields] — subset of field keys to expose
 *   (defaults to all three). Order follows TIER_FIELDS.
 * @param {boolean} [props.showHint=true] — render the per-panel explanatory
 *   hint. Set false when several panels sit side by side and share one hint
 *   above the row (keeps the columns aligned + uncluttered).
 */
export function IonTierLimitsPanel({ tier, heading = 'Ion (AI chat) limits', fields, showHint = true }) {
  const [limits, setLimits] = useState(null); // full GET payload
  const [drafts, setDrafts] = useState({}); // in-progress edits (string values)
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const shownFields = TIER_FIELDS.filter(
    (f) => !fields || fields.includes(f.key)
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  function applyLimits(data) {
    setLimits(data);
    const seeded = {};
    const tierVals = data?.[tier] || {};
    shownFields.forEach((f) => {
      seeded[f.key] = tierVals[f.key] != null ? String(tierVals[f.key]) : '';
    });
    setDrafts(seeded);
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      applyLimits(await getChatTierLimits());
    } catch (err) {
      console.error('[IonTierLimits] Load error:', err);
      setError(err?.message || 'Failed to load Ion tier limits');
    } finally {
      setLoading(false);
    }
  }

  function flashSuccess(msg) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  }

  function parseValue(field, raw) {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') return { error: 'Enter a value.' };
    const value = field.kind === 'int' ? parseInt(trimmed, 10) : parseFloat(trimmed);
    if (!Number.isFinite(value) || value < field.min || value > field.max) {
      return {
        error:
          field.kind === 'int'
            ? `Enter a whole number between ${field.min} and ${field.max}.`
            : `Enter a number between ${field.min} and ${field.max}.`,
      };
    }
    return { value };
  }

  async function saveField(field) {
    const parsed = parseValue(field, drafts[field.key]);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    setSavingField(field.key);
    try {
      // Send the tier + ONLY the changed field.
      const next = await updateChatTierLimits({ tier, [field.key]: parsed.value });
      applyLimits(next);
      flashSuccess('Saved. Applies to new requests within about a minute.');
    } catch (err) {
      console.error('[IonTierLimits] Save error:', err);
      setError(err?.message || 'Failed to save');
    } finally {
      setSavingField(null);
    }
  }

  async function resetField(field) {
    setError('');
    setSavingField(field.key);
    try {
      // Sending null clears the DB override → reverts to the env default.
      const next = await updateChatTierLimits({ tier, [field.key]: null });
      applyLimits(next);
      flashSuccess('Reverted to default. Applies within about a minute.');
    } catch (err) {
      console.error('[IonTierLimits] Reset error:', err);
      setError(err?.message || 'Failed to reset');
    } finally {
      setSavingField(null);
    }
  }

  const overridden = limits?.overridden?.[tier] || {};
  const tierVals = limits?.[tier] || {};

  return (
    <div className="admin-ion-limits">
      <div className="admin-section-header">
        <h2>{heading}</h2>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={load}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {showHint && (
        <p className="admin-demo-hint">
          Tune Ion’s <strong>{tier.toUpperCase()}</strong> limits live — no deploy
          needed. Changes apply to new requests within about a minute. A field
          showing{' '}
          <span className="admin-demo-badge admin-demo-badge--override">overridden</span>{' '}
          uses this value; <span className="admin-demo-badge">default</span> falls
          back to the environment default.
        </p>
      )}

      {success && <div className="admin-alert admin-alert--success">{success}</div>}
      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {loading ? (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading Ion limits…</p>
        </div>
      ) : limits ? (
        <div className="admin-demo-config-grid">
          {shownFields.map((field) => {
            const isOverride = overridden[field.key] === true;
            const busy = savingField === field.key;
            const draftVal = drafts[field.key] ?? '';
            const dirty = String(draftVal) !== String(tierVals[field.key] ?? '');
            return (
              <div className="admin-demo-config-row" key={field.key}>
                <div className="admin-demo-config-labels">
                  <label
                    className="admin-form-label admin-demo-config-label"
                    htmlFor={`ion-lim-${tier}-${field.key}`}
                  >
                    {field.label}
                    <span
                      className={`admin-demo-badge${isOverride ? ' admin-demo-badge--override' : ''}`}
                    >
                      {isOverride ? 'overridden' : 'default'}
                    </span>
                  </label>
                  <p className="admin-demo-config-help">{field.help}</p>
                </div>
                <div className="admin-demo-config-controls">
                  <input
                    id={`ion-lim-${tier}-${field.key}`}
                    className="admin-demo-input"
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={draftVal}
                    disabled={busy}
                    onInput={(e) =>
                      setDrafts({ ...drafts, [field.key]: e.target.value })
                    }
                  />
                  <button
                    className="admin-btn admin-btn--primary"
                    onClick={() => saveField(field)}
                    disabled={busy || !dirty}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="admin-btn admin-btn--secondary"
                    onClick={() => resetField(field)}
                    disabled={busy || !isOverride}
                    title={
                      isOverride
                        ? 'Clear the override and revert to the environment default'
                        : 'Already using the environment default'
                    }
                  >
                    Reset to default
                  </button>
                  {field.tokenKey && tierVals[field.tokenKey] != null && (
                    <span className="admin-demo-config-token-hint">
                      ≈ {tierVals[field.tokenKey]} tokens
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
